import { useMemo } from 'react'
import { Box, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { Source, Layer } from 'react-map-gl/maplibre'
import { TurfMap } from '../../../turf/TurfMap'
import { useTurfMapDetail, useTurfMapHouseholds } from '../../../../hooks/useTurfMap'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { getTurfColor } from '../../../../lib/mapConfig'
import { TURF_MAP_COLORS } from '../../../../theme/turfMapColors'
import type { TurfBoundary } from '../../../../types/turfMap'
import type { FollowUpRouteDto } from '../../../../types/followUp'

interface FollowUpRoutePreviewMapProps {
  turfMapId: string
  routes: FollowUpRouteDto[]
}

/** Read-only preview of one follow-up batch: route outlines + stops coloured by route. No draw/edit
 *  tools and no boundary-click editing — routes are re-cut, never hand-edited, from here. */
export function FollowUpRoutePreviewMap({ turfMapId, routes }: FollowUpRoutePreviewMapProps) {
  const isMobile = useIsMobile()
  const { data: detail, isLoading: detailLoading } = useTurfMapDetail(turfMapId)
  const { data: households, isLoading: householdsLoading } = useTurfMapHouseholds(turfMapId)

  // TurfDto carries no turfIndex, so the route list supplies it (by turf id).
  const indexByTurf = useMemo(() => new Map(routes.map(r => [r.turfId, r.turfIndex])), [routes])

  const boundaries: TurfBoundary[] = useMemo(() => {
    const out: TurfBoundary[] = []
    for (const [i, t] of (detail?.turfs ?? []).entries()) {
      if (!t.boundaryGeoJson) continue
      try {
        out.push({
          id: t.id,
          label: t.label,
          color: getTurfColor(indexByTurf.get(t.id) ?? i),
          geoJson: { type: 'Feature', geometry: JSON.parse(t.boundaryGeoJson), properties: {} },
        })
      } catch {
        // skip an unparsable boundary
      }
    }
    return out
  }, [detail, indexByTurf])

  const stops: GeoJSON.FeatureCollection<GeoJSON.Point> = useMemo(() => ({
    type: 'FeatureCollection',
    features: (households ?? []).map(h => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [h.longitude, h.latitude] },
      properties: { id: h.householdKey, color: getTurfColor(h.contacts[0]?.turfIndex ?? 0) },
    })),
  }), [households])

  if (detailLoading || householdsLoading) {
    return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  }

  return (
    <VStack align="stretch" gap={2}>
      <Box mx={{ base: -4, md: 0 }} borderRadius={{ base: 0, md: 'md' }} overflow="hidden" borderWidth="1px" borderColor="border">
        <TurfMap boundaries={boundaries} fitToBounds height={isMobile ? 320 : 420} disableInteractions={false}>
          <Source id="followup-route-stops" type="geojson" data={stops}>
            <Layer
              id="followup-route-stops-circle"
              type="circle"
              paint={{
                'circle-color': ['get', 'color'],
                'circle-radius': 6,
                'circle-stroke-width': 1.5,
                'circle-stroke-color': TURF_MAP_COLORS.addInStroke, // theme-resolved white
              }}
            />
          </Source>
        </TurfMap>
      </Box>
      <HStack gap={4} flexWrap="wrap" aria-label="Route legend">
        {routes.map(r => (
          <HStack key={r.turfId} gap={2}>
            <Box w="12px" h="12px" borderRadius="full" bg={getTurfColor(r.turfIndex)} flexShrink={0} />
            <Text fontSize="sm">{r.label}</Text>
            <Text fontSize="xs" color="muted">{r.stopCount === 1 ? '1 stop' : `${r.stopCount} stops`}</Text>
          </HStack>
        ))}
      </HStack>
    </VStack>
  )
}
